import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { 
  Phone, MapPin, ChevronRight, CheckCircle, Clock, 
  Shield, Star, ArrowLeft, ChevronDown
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import QuoteForm from "../components/QuoteForm";
import HeroBanner from "../components/HeroBanner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Static departments data with full details
const STATIC_DEPARTMENTS_DATA = {
  "paris-75": {
    id: "1", name: "Paris", code: "75", slug: "paris-75",
    description: "Serrurier Fichet à Paris (75) - Intervention rapide 24h/24 et 7j/7 dans tous les arrondissements parisiens. Nos techniciens spécialisés Fichet interviennent pour l'ouverture de porte blindée, le remplacement de cylindre haute sécurité, la réparation de serrure multipoints et le dépannage d'urgence. Que vous soyez dans le Marais, à Montmartre, dans le 16ème ou à Bastille, nous arrivons en 30 minutes en moyenne.",
    cities: ["Paris 1er", "Paris 2ème", "Paris 3ème", "Paris 4ème", "Paris 5ème", "Paris 6ème", "Paris 7ème", "Paris 8ème", "Paris 9ème", "Paris 10ème", "Paris 11ème", "Paris 12ème", "Paris 13ème", "Paris 14ème", "Paris 15ème", "Paris 16ème", "Paris 17ème", "Paris 18ème", "Paris 19ème", "Paris 20ème"],
    faqs: [
      { question: "Combien coûte un serrurier Fichet à Paris ?", answer: "Le tarif d'un serrurier Fichet à Paris varie selon l'intervention : ouverture de porte de 90€ à 180€, remplacement de cylindre de 280€ à 550€. Nous proposons un devis gratuit avant toute intervention." },
      { question: "Quel est le délai d'intervention à Paris ?", answer: "Nos techniciens Fichet interviennent en 30 minutes en moyenne dans Paris intra-muros." },
      { question: "Intervenez-vous la nuit à Paris ?", answer: "Oui, notre service de dépannage Fichet est disponible 24h/24 à Paris, y compris les nuits, week-ends et jours fériés." },
      { question: "Pouvez-vous ouvrir une porte blindée Fichet sans la casser ?", answer: "Oui, nos techniciens sont formés aux techniques d'ouverture fine. Dans 95% des cas, nous ouvrons les portes Fichet sans aucun dégât." }
    ]
  },
  "hauts-de-seine-92": {
    id: "2", name: "Hauts-de-Seine", code: "92", slug: "hauts-de-seine-92",
    description: "Serrurier Fichet dans les Hauts-de-Seine (92) - Dépannage rapide à Boulogne-Billancourt, Neuilly-sur-Seine, Levallois-Perret, Nanterre, Courbevoie, Colombes, Asnières et toutes les communes du 92. Serrurier Fichet Boulogne, dépannage serrure Neuilly, urgence serrurerie Levallois disponible 24h/24.",
    cities: ["Boulogne-Billancourt", "Neuilly-sur-Seine", "Levallois-Perret", "Nanterre", "Courbevoie", "Colombes", "Asnières-sur-Seine", "Rueil-Malmaison", "Issy-les-Moulineaux", "Antony", "Clamart", "Meudon", "Montrouge", "Suresnes", "Puteaux"],
    faqs: [
      { question: "Intervenez-vous rapidement à Boulogne-Billancourt ?", answer: "Oui, nous intervenons à Boulogne-Billancourt en 20 à 30 minutes. Nos techniciens Fichet connaissent parfaitement le secteur." },
      { question: "Quel est le prix d'un dépannage Fichet à Neuilly-sur-Seine ?", answer: "Les tarifs à Neuilly sont identiques au reste du 92 : ouverture de porte à partir de 90€, remplacement cylindre dès 280€." },
      { question: "Pouvez-vous intervenir en urgence à Levallois-Perret ?", answer: "Absolument. Notre service d'urgence Fichet est disponible 24h/24 à Levallois-Perret." },
      { question: "Couvrez-vous Courbevoie et La Défense ?", answer: "Oui, nous intervenons à Courbevoie, La Défense et dans tout le quartier d'affaires." }
    ]
  },
  "seine-saint-denis-93": {
    id: "3", name: "Seine-Saint-Denis", code: "93", slug: "seine-saint-denis-93",
    description: "Serrurier Fichet en Seine-Saint-Denis (93) - Intervention rapide à Saint-Denis, Montreuil, Bobigny, Aubervilliers, Pantin, Aulnay-sous-Bois. Serrurier Fichet Montreuil, dépannage serrure Saint-Denis, urgence Pantin - disponible 7j/7 et 24h/24.",
    cities: ["Saint-Denis", "Montreuil", "Bobigny", "Aubervilliers", "Pantin", "Aulnay-sous-Bois", "Drancy", "Noisy-le-Grand", "Bondy", "Épinay-sur-Seine", "Sevran", "Livry-Gargan", "Le Blanc-Mesnil", "Saint-Ouen", "Rosny-sous-Bois"],
    faqs: [
      { question: "Quel est le délai d'intervention à Montreuil ?", answer: "Nous intervenons à Montreuil en 25 à 35 minutes selon le quartier." },
      { question: "Intervenez-vous après un cambriolage à Saint-Denis ?", answer: "Oui, nous assurons la mise en sécurité immédiate après cambriolage à Saint-Denis." },
      { question: "Combien coûte un serrurier Fichet à Bobigny ?", answer: "Nos tarifs à Bobigny : ouverture de porte dès 90€, remplacement cylindre Fichet dès 280€." },
      { question: "Dépannez-vous les serrures Fichet à Pantin ?", answer: "Oui, nous sommes spécialistes Fichet à Pantin. Intervention sur tous types de serrures." }
    ]
  },
  "val-de-marne-94": {
    id: "4", name: "Val-de-Marne", code: "94", slug: "val-de-marne-94",
    description: "Serrurier Fichet dans le Val-de-Marne (94) - Dépannage et installation à Créteil, Vincennes, Saint-Maur-des-Fossés, Vitry-sur-Seine, Champigny-sur-Marne. Serrurier Fichet Vincennes, dépannage serrure Créteil, urgence Saint-Maur - intervention 24h/24.",
    cities: ["Créteil", "Vincennes", "Saint-Maur-des-Fossés", "Vitry-sur-Seine", "Champigny-sur-Marne", "Ivry-sur-Seine", "Maisons-Alfort", "Fontenay-sous-Bois", "Villejuif", "Nogent-sur-Marne", "Le Perreux-sur-Marne", "Alfortville", "Charenton-le-Pont", "Joinville-le-Pont", "Choisy-le-Roi"],
    faqs: [
      { question: "Intervenez-vous rapidement à Vincennes ?", answer: "Oui, intervention à Vincennes en 20 à 30 minutes. Proximité immédiate de Paris." },
      { question: "Quel est le prix d'une ouverture de porte à Créteil ?", answer: "L'ouverture de porte Fichet à Créteil coûte entre 90€ et 180€ selon la complexité." },
      { question: "Dépannez-vous à Saint-Maur-des-Fossés le week-end ?", answer: "Oui, notre service Fichet est disponible 7j/7 à Saint-Maur-des-Fossés." },
      { question: "Pouvez-vous changer un cylindre Fichet à Champigny ?", answer: "Absolument. Remplacement de cylindre Fichet à Champigny-sur-Marne. Stock de pièces disponible." }
    ]
  },
  "yvelines-78": {
    id: "5", name: "Yvelines", code: "78", slug: "yvelines-78",
    description: "Serrurier Fichet dans les Yvelines (78) - Intervention rapide à Versailles, Saint-Germain-en-Laye, Sartrouville, Poissy. Serrurier Fichet Versailles, dépannage serrure Saint-Germain, urgence Sartrouville. Service 24h/24 et 7j/7.",
    cities: ["Versailles", "Saint-Germain-en-Laye", "Sartrouville", "Poissy", "Conflans-Sainte-Honorine", "Mantes-la-Jolie", "Les Mureaux", "Houilles", "Chatou", "Le Chesnay-Rocquencourt", "Montigny-le-Bretonneux", "Rambouillet", "Maisons-Laffitte", "Le Vésinet", "Plaisir"],
    faqs: [
      { question: "Intervenez-vous à Versailles rapidement ?", answer: "Oui, intervention à Versailles en 30-40 minutes." },
      { question: "Quel est le tarif d'un serrurier Fichet à Saint-Germain-en-Laye ?", answer: "Tarifs standards du 78 : ouverture dès 90€, remplacement cylindre dès 280€." },
      { question: "Dépannez-vous les serrures Fichet à Sartrouville ?", answer: "Oui, dépannage Fichet à Sartrouville et communes limitrophes." },
      { question: "Pouvez-vous intervenir à Poissy en urgence ?", answer: "Absolument. Service d'urgence Fichet 24h/24 à Poissy." }
    ]
  },
  "val-doise-95": {
    id: "6", name: "Val-d'Oise", code: "95", slug: "val-doise-95",
    description: "Serrurier Fichet dans le Val-d'Oise (95) - Dépannage rapide à Argenteuil, Cergy, Sarcelles, Pontoise, Franconville. Serrurier Fichet Argenteuil, dépannage serrure Cergy, urgence Sarcelles. Disponible 24h/24 et 7j/7.",
    cities: ["Argenteuil", "Cergy", "Sarcelles", "Pontoise", "Franconville", "Garges-lès-Gonesse", "Ermont", "Bezons", "Goussainville", "Herblay-sur-Seine", "Eaubonne", "Saint-Ouen-l'Aumône", "Taverny", "Villiers-le-Bel", "Montmorency"],
    faqs: [
      { question: "Quel est le délai d'intervention à Argenteuil ?", answer: "Intervention à Argenteuil en 25-35 minutes." },
      { question: "Intervenez-vous à Cergy-Pontoise ?", answer: "Oui, couverture complète de Cergy-Pontoise." },
      { question: "Combien coûte un dépannage Fichet à Sarcelles ?", answer: "Tarifs à Sarcelles : ouverture de porte dès 90€, remplacement cylindre dès 280€." },
      { question: "Dépannez-vous les week-ends à Franconville ?", answer: "Oui, service Fichet disponible 7j/7 à Franconville." }
    ]
  },
  "essonne-91": {
    id: "7", name: "Essonne", code: "91", slug: "essonne-91",
    description: "Serrurier Fichet en Essonne (91) - Intervention à Évry-Courcouronnes, Corbeil-Essonnes, Massy, Palaiseau. Serrurier Fichet Évry, dépannage serrure Massy, urgence Corbeil. Service 24h/24 et 7j/7.",
    cities: ["Évry-Courcouronnes", "Corbeil-Essonnes", "Massy", "Palaiseau", "Savigny-sur-Orge", "Sainte-Geneviève-des-Bois", "Viry-Châtillon", "Athis-Mons", "Grigny", "Ris-Orangis", "Yerres", "Draveil", "Brunoy", "Les Ulis", "Longjumeau"],
    faqs: [
      { question: "Intervenez-vous rapidement à Évry-Courcouronnes ?", answer: "Oui, intervention à Évry en 30-40 minutes." },
      { question: "Quel est le prix d'un serrurier Fichet à Massy ?", answer: "Tarifs à Massy : ouverture dès 90€, cylindre dès 280€." },
      { question: "Dépannez-vous à Corbeil-Essonnes le soir ?", answer: "Oui, service de nuit disponible à Corbeil-Essonnes." },
      { question: "Pouvez-vous ouvrir une porte Fichet à Palaiseau ?", answer: "Absolument. Ouverture de porte Fichet à Palaiseau sans dégât." }
    ]
  },
  "seine-et-marne-77": {
    id: "8", name: "Seine-et-Marne", code: "77", slug: "seine-et-marne-77",
    description: "Serrurier Fichet en Seine-et-Marne (77) - Dépannage rapide à Meaux, Chelles, Melun, Pontault-Combault. Serrurier Fichet Meaux, dépannage serrure Chelles, urgence Melun. Intervention 24h/24 et 7j/7.",
    cities: ["Meaux", "Chelles", "Melun", "Pontault-Combault", "Savigny-le-Temple", "Torcy", "Lagny-sur-Marne", "Bussy-Saint-Georges", "Roissy-en-Brie", "Villeparisis", "Combs-la-Ville", "Ozoir-la-Ferrière", "Dammarie-les-Lys", "Le Mée-sur-Seine", "Noisiel"],
    faqs: [
      { question: "Intervenez-vous à Meaux rapidement ?", answer: "Oui, intervention à Meaux en 35-45 minutes." },
      { question: "Quel est le tarif d'un dépannage Fichet à Chelles ?", answer: "Tarifs à Chelles : ouverture de porte dès 90€, remplacement cylindre dès 280€." },
      { question: "Dépannez-vous les serrures Fichet à Melun ?", answer: "Oui, dépannage Fichet à Melun et sud du 77." },
      { question: "Pouvez-vous intervenir à Marne-la-Vallée ?", answer: "Absolument. Couverture de Marne-la-Vallée : Torcy, Noisiel, Lognes, Bussy-Saint-Georges." }
    ]
  }
};

const STATIC_DEPARTMENTS_LIST = Object.values(STATIC_DEPARTMENTS_DATA);

export default function DepartmentPage() {
  const { slug } = useParams();
  const [department, setDepartment] = useState(null);
  const [config, setConfig] = useState(null);
  const [services, setServices] = useState([]);
  const [departments, setDepartments] = useState(STATIC_DEPARTMENTS_LIST);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // First, set static data immediately so page renders fast
    const staticDept = STATIC_DEPARTMENTS_DATA[slug];
    if (staticDept) {
      setDepartment(staticDept);
      setLoading(false);
    }

    // Then try to fetch from API
    Promise.all([
      fetch(`${API_URL}/api/departments/${slug}`).then(res => res.ok ? res.json() : null).catch(() => null),
      fetch(`${API_URL}/api/config`).then(res => res.json()).catch(() => null),
      fetch(`${API_URL}/api/services`).then(res => res.json()).catch(() => []),
      fetch(`${API_URL}/api/departments`).then(res => res.json()).catch(() => [])
    ])
      .then(([deptData, configData, servicesData, deptsData]) => {
        // Only update if API returned valid data
        if (deptData && deptData.name) {
          setDepartment(deptData);
        }
        if (configData) {
          setConfig(configData);
        }
        if (servicesData && servicesData.length > 0) {
          setServices(servicesData);
        }
        if (deptsData && deptsData.length > 0) {
          setDepartments(deptsData);
        }
        setLoading(false);
      })
      .catch(() => {
        // If API fails, we already have static data
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!department) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[#0f2744] mb-4">Département non trouvé</h1>
          <Link to="/" className="btn-primary">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  const otherDepartments = departments.filter(d => d.slug !== slug);

  return (
    <div data-testid="department-page">
      {/* Hero */}
      <HeroBanner
        title={`Serrurier Fichet ${department.name}`}
        subtitle={department.description}
        highlightText="Fichet"
        showFeatures={false}
        showCTA={true}
        config={config}
        variant="compact"
        breadcrumb={
          <div className="flex items-center gap-2 text-white/80">
            <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Département {department.code}</span>
          </div>
        }
      />

      {/* Trust indicators */}
      <section className="bg-white py-6 border-b border-gray-100">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-8 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-5 h-5 text-orange-500" />
              Intervention rapide dans le {department.code}
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Devis gratuit
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Shield className="w-5 h-5 text-blue-500" />
              7j/7 - 24h/24
            </div>
          </div>
        </div>
      </section>

      {/* Main content */}
      <section className="section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Content */}
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-[#0f2744] mb-6">
                Dépannage de serrures Fichet dans le {department.code} - {department.name}
              </h2>
              
              <p className="text-gray-600 mb-6">
                Vous recherchez un <strong>serrurier spécialiste Fichet dans le {department.code}</strong> ? 
                Notre équipe de techniciens qualifiés intervient rapidement dans tout le département 
                {department.name} pour tous vos problèmes de serrurerie.
              </p>

              <p className="text-gray-600 mb-8">
                Que vous ayez besoin d'une <strong>ouverture de porte</strong>, d'un 
                <strong> remplacement de cylindre Fichet</strong> ou d'une réparation de serrure, 
                nous intervenons 7 jours sur 7, 24 heures sur 24.
              </p>

              {/* Cities covered */}
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <h3 className="text-xl font-bold text-[#0f2744] mb-4 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-orange-500" />
                  Villes d'intervention dans le {department.code}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {department.cities.map((city, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 bg-white border border-gray-200 rounded-full text-sm text-gray-600"
                    >
                      {city}
                    </span>
                  ))}
                  <span className="px-3 py-1 bg-orange-100 text-orange-600 rounded-full text-sm font-medium">
                    + autres villes
                  </span>
                </div>
              </div>

              {/* Services */}
              <h3 className="text-2xl font-bold text-[#0f2744] mb-4">
                Nos services dans le {department.code}
              </h3>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {services.map((service) => (
                  <Link
                    key={service.id}
                    to={`/services/${service.slug}`}
                    className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-orange-50 hover:border-orange-200 border border-transparent transition-all group"
                  >
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-gray-700 group-hover:text-orange-700">{service.name}</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 ml-auto group-hover:translate-x-1 transition-transform" />
                  </Link>
                ))}
              </div>

              {/* FAQ Section */}
              <h3 className="text-2xl font-bold text-[#0f2744] mb-4">
                Questions fréquentes - {department.name} ({department.code})
              </h3>

              <Accordion type="single" collapsible className="space-y-3 mb-8">
                {department.faqs.map((faq, index) => (
                  <AccordionItem
                    key={index}
                    value={`faq-${index}`}
                    className="bg-white border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <AccordionTrigger className="px-5 py-4 text-left hover:no-underline hover:bg-gray-50 [&[data-state=open]>svg]:rotate-180">
                      <span className="text-[#0f2744] font-semibold pr-4">
                        {faq.question}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="px-5 pb-4 text-gray-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>

              {/* Why choose us */}
              <h3 className="text-2xl font-bold text-[#0f2744] mb-4">
                Pourquoi choisir Fichet Dépannage dans le {department.code} ?
              </h3>

              <div className="space-y-4">
                {[
                  `Intervention rapide dans tout le ${department.name}`,
                  "Techniciens formés et expérimentés sur les serrures Fichet",
                  "Ouverture sans dégât dans 95% des cas",
                  "Devis gratuit et transparent avant intervention",
                  "Pièces de qualité avec garantie",
                  "Disponible 7j/7 et 24h/24"
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                    <span className="text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              {/* Quote Form */}
              <div className="sticky top-24">
                <QuoteForm services={services} cities={department.cities.map(c => ({id: c, name: c}))} />

                {/* Other departments */}
                <div className="mt-6 bg-gray-50 rounded-2xl p-6">
                  <h3 className="font-bold text-[#0f2744] mb-4">
                    Autres départements
                  </h3>
                  <div className="space-y-2">
                    {otherDepartments.map((d) => (
                      <Link
                        key={d.id}
                        to={`/departement/${d.slug}`}
                        className="flex items-center justify-between py-2 px-3 bg-white rounded-lg text-gray-600 hover:text-orange-600 hover:bg-orange-50 transition-colors text-sm"
                      >
                        <span>{d.name} ({d.code})</span>
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-amber-50 border-y border-amber-200 py-6">
        <div className="container-custom">
          <p className="text-center text-amber-800 text-sm">
            <strong>⚠️ Entreprise indépendante</strong> spécialisée dans le dépannage de serrures Fichet. 
            Nous ne faisons pas partie du réseau officiel Point Fort Fichet.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-[#0f2744] to-[#1a3a5c] py-12 text-white">
        <div className="container-custom text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">
            Serrurier Fichet {department.name} - Intervention rapide
          </h2>
          <p className="text-gray-300 mb-6 max-w-xl mx-auto">
            Appelez-nous maintenant pour une intervention rapide dans le {department.code}.
          </p>
          {config && (
            <a
              href={config.phone_link} onClick={() => { if(window.gtag_report_conversion) window.gtag_report_conversion(config.phone_link); }}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-10 py-5 rounded-xl font-bold text-2xl shadow-xl hover:from-orange-600 hover:to-orange-700 transition-all"
              data-testid="dept-cta-phone"
            >
              <Phone className="w-7 h-7" />
              {config.phone}
            </a>
          )}
        </div>
      </section>

      {/* Schema.org structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": `Fichet Dépannage ${department.name}`,
          "description": department.meta_description,
          "telephone": config?.phone || "09 72 57 44 50",
          "address": {
            "@type": "PostalAddress",
            "addressRegion": department.name,
            "addressCountry": "FR"
          },
          "areaServed": department.cities,
          "openingHoursSpecification": {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            "opens": "00:00",
            "closes": "23:59"
          }
        })
      }} />
    </div>
  );
}
